import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * As tarefas do dia (Fase 18, fatia 3).
 *
 * ```text
 * Tarefas Realizadas
 * Descrição                                    Qtd. Medida  Unidade  Observações
 * Construção de alvenaria - 7° pavimento            28,96      m2     Responsáveis: …
 * Limpeza e organização do canteiro                                   Responsáveis: …
 * ```
 *
 * ## Duas origens, e por que não podem ser uma só
 *
 * Metade das tarefas do RDO de referência **não é frente do cronograma**: `Produção de
 * argamassa`, `Transporte de insumos`, `Limpeza e organização do canteiro`. Nenhuma tem
 * pavimento, nenhuma tem disciplina, nenhuma tem medição. O sistema só sabe falar de
 * `BoardActivity`, que é Pavimento × Disciplina — e forçar essas quatro a virarem frentes
 * encheria a árvore da obra de linhas que não são obra.
 *
 * Então **a frente é derivada e a avulsa é guardada**. A frente sai das conferências do
 * dia e ninguém a digita; a avulsa é linha livre, existe só dentro deste documento, e não
 * entra em apuração nenhuma (RN-213).
 *
 * ## A transposição
 *
 * A conferência é por **pessoa**: quatorze linhas, cada uma com as frentes daquela pessoa.
 * O RDO é por **frente**: uma linha por serviço, com quem o fez. É a mesma informação
 * virada do avesso, e é por isso que `groupExecutedFronts` recebe um registro plano por
 * `(pessoa, frente)` — que é exatamente a granularidade de `WeeklyAllocation`.
 *
 * O `Márcio = 0,71 / Maicon = 1` que no RDO de referência foi digitado à mão dentro do
 * campo de observação **o sistema já sabe**: `executedQuantity` é por recurso, frente e
 * dia. Ninguém digita de novo o que a conferência já mediu.
 */

/** Quem fez, e quanto dessa frente coube a essa pessoa — ou a essa equipe (Fase 30). */
export interface FrontWorker {
  readonly name: string;
  /**
   * A função — `pedreiro`, `carpinteiro`. Para serviço terceirizado, a empresa. Para a
   * equipe, `Equipe`.
   */
  readonly role: string;
  /** Ausente = a frente não é medida, ou ninguém mediu a parte desta pessoa. */
  readonly quantity?: number;
  /**
   * Quanto da quantidade não está conforme (Fase 37, RN-475): o RDO mostra a medida e a não
   * conforme. Ausente = nenhuma NC com quantidade nesta medição.
   */
  readonly nonConformingQuantity?: number;
  /**
   * Quem da equipe trabalhou na frente no dia — presente só na linha da medição da equipe
   * (D5). A quantidade é da equipe, e nada é repartido entre eles: o RDO diz quem estava lá.
   */
  readonly members?: readonly string[];
}

/**
 * "Equipe Alvenaria 01": o nome com que a equipe sai no RDO. A equipe que já se chama
 * "Equipe …" não ganha a palavra duas vezes.
 */
export function teamWorkerName(teamName: string): string {
  return /^equipe\b/i.test(teamName.trim()) ? teamName.trim() : `Equipe ${teamName.trim()}`;
}

/** Um registro por `(pessoa, frente, serviço)` — a granularidade da alocação (RN-231). */
export interface ExecutedFrontRow {
  readonly activityId: string;
  /**
   * O serviço da linha. Ausente é a alocação sem serviço, que o papel ainda descreve
   * pela frente.
   */
  readonly serviceId?: string;
  readonly description: string;
  readonly unitName?: string;
  readonly unitDecimals?: number;
  /**
   * O serviço mede peça (estaca, bloco…): a quantidade é da **frente**, as pessoas lançam o
   * mesmo número, e a linha leva a maior — e não a soma (07/10/2026). Ausente, cada pessoa
   * lança a própria produção, e a linha soma.
   */
  readonly byFront?: boolean;
  readonly worker: FrontWorker;
}

/** Uma linha de "Tarefas Realizadas" que veio do cronograma. */
export interface ExecutedFrontLine {
  readonly activityId: string;
  readonly serviceId?: string;
  readonly description: string;
  readonly unitName?: string;
  readonly unitDecimals?: number;
  /**
   * A soma do que as pessoas mediram.
   *
   * **Ausente quando ninguém mediu**, e não zero: frente não medida e frente medida em
   * zero são coisas diferentes, e no papel a primeira é uma célula vazia enquanto a
   * segunda é a afirmação de que não se produziu nada.
   */
  readonly quantity?: number;
  /**
   * A soma do que não está conforme (Fase 37, RN-475) — ou a maior, no serviço que mede peça,
   * como a quantidade. Ausente quando nenhuma medição da frente tem.
   */
  readonly nonConformingQuantity?: number;
  readonly workers: readonly FrontWorker[];
}

/**
 * A tarefa que não está no cronograma (RN-213).
 *
 * Não tem frente, e por isso não entra em apuração nenhuma: uma "limpeza do canteiro"
 * somada ao planejado × realizado da Fase 10 seria serviço planejado que ninguém planejou.
 *
 * Desde a Fase 20 ela carrega o que o RDO de referência escreve ao lado dela — quem fez,
 * quanto (`6 und`, `1,71 m²`) e quantas horas —, e tudo isso é **texto do dia**: a
 * quantidade não tem unidade do catálogo, e as horas não pesam na jornada nem na
 * capacidade. É o registro do que aconteceu, para o papel; o número da obra continua
 * saindo da frente conferida.
 */
export interface DailyReportTask {
  readonly description: string;
  readonly notes?: string;
  /** Quem fez, pelo nome — `Carlos Henrique (auxiliar de obra)`, como o Vienna lista. */
  readonly workers?: readonly string[];
  /** Quantidade e unidade em texto do dia; nenhuma das duas vem do catálogo. */
  readonly quantity?: number;
  readonly unit?: string;
  /** Horas gastas, em quartos de hora. Não pesam na capacidade (RN-213). */
  readonly quarterHours?: number;
}

export const MAX_REPORT_TASKS = 30;
export const MAX_TASK_DESCRIPTION = 200;
export const MAX_TASK_NOTES = 500;
export const MAX_REPORT_NOTES = 2000;
export const MAX_TASK_WORKERS = 10;
export const MAX_TASK_WORKER_NAME = 80;
export const MAX_TASK_UNIT = 20;
/** 24 horas: mais do que isso não é um dia. */
export const MAX_TASK_QUARTER_HOURS = 96;

/**
 * Agrupa por frente **e serviço** o que a conferência registrou por pessoa.
 *
 * A ordem é a da descrição, e não a da chegada: o documento é lido de cima a baixo por
 * quem não acompanhou o dia, e a ordem em que as conferências foram gravadas não diz nada
 * a essa pessoa.
 *
 * ## Por que o serviço entrou na chave
 *
 * Agrupar só por frente estava certo enquanto havia uma linha por frente. Desde que o
 * serviço entrou na identidade da alocação (RN-231), a mesma frente tem assentamento e
 * verga no mesmo dia — e somá-los daria um número que não existe: doze metros quadrados
 * mais três unidades sairiam impressos como quinze metros quadrados, na unidade de quem
 * chegou primeiro. Número errado no documento que vai para fora é pior do que número
 * nenhum.
 */
export function groupExecutedFronts(
  rows: readonly ExecutedFrontRow[],
): readonly ExecutedFrontLine[] {
  const porFrente = new Map<string, ExecutedFrontLine>();

  for (const row of rows) {
    const chave = `${row.activityId}|${row.serviceId ?? ''}`;
    const atual = porFrente.get(chave);
    if (atual === undefined) {
      porFrente.set(chave, {
        activityId: row.activityId,
        ...(row.serviceId === undefined ? {} : { serviceId: row.serviceId }),
        description: row.description,
        workers: [row.worker],
        ...(row.unitName === undefined ? {} : { unitName: row.unitName }),
        ...(row.unitDecimals === undefined ? {} : { unitDecimals: row.unitDecimals }),
        ...(row.worker.quantity === undefined ? {} : { quantity: row.worker.quantity }),
        ...(row.worker.nonConformingQuantity === undefined
          ? {}
          : { nonConformingQuantity: row.worker.nonConformingQuantity }),
      });
      continue;
    }

    // `undefined + 5` seria `NaN`, e `0 + 5` mentiria sobre quem não mediu. A soma só
    // existe a partir da primeira medição de verdade.
    // O serviço que mede peça é da frente: as pessoas marcam as mesmas peças, e vale a maior.
    const somada =
      row.worker.quantity === undefined
        ? atual.quantity
        : row.byFront === true
          ? Math.max(atual.quantity ?? 0, row.worker.quantity)
          : (atual.quantity ?? 0) + row.worker.quantity;

    const naoConforme =
      row.worker.nonConformingQuantity === undefined
        ? atual.nonConformingQuantity
        : row.byFront === true
          ? Math.max(atual.nonConformingQuantity ?? 0, row.worker.nonConformingQuantity)
          : (atual.nonConformingQuantity ?? 0) + row.worker.nonConformingQuantity;

    porFrente.set(chave, {
      ...atual,
      workers: [...atual.workers, row.worker],
      ...(somada === undefined ? {} : { quantity: somada }),
      ...(naoConforme === undefined ? {} : { nonConformingQuantity: naoConforme }),
    });
  }

  return [...porFrente.values()]
    .map((linha) => ({
      ...linha,
      workers: [...linha.workers].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    }))
    .sort((a, b) => a.description.localeCompare(b.description, 'pt-BR'));
}

/**
 * Valida as tarefas avulsas que a tela mandou.
 *
 * Linha em branco é **descartada**, não recusada: a tela mostra campos vazios para serem
 * preenchidos, e quem deixou um sem preencher não cometeu erro nenhum. É o mesmo
 * tratamento do turno vazio da fatia 2 (RN-211).
 */
export function parseReportTasks(
  entradas: readonly DailyReportTask[],
): Result<readonly DailyReportTask[], DomainError> {
  const tarefas: DailyReportTask[] = [];

  for (const entrada of entradas) {
    const descricao = entrada.description.trim();
    const observacao = entrada.notes?.trim() ?? '';

    // Observação sem descrição não é tarefa: é uma frase solta que ninguém sabe do quê.
    if (descricao === '') continue;

    if (descricao.length > MAX_TASK_DESCRIPTION) {
      return err(
        domainError(
          PLANNING_ERROR.TASK_DESCRIPTION_TOO_LONG,
          PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_DESCRIPTION_TOO_LONG]!,
          { description: descricao.slice(0, 40) },
        ),
      );
    }
    if (observacao.length > MAX_TASK_NOTES) {
      return err(
        domainError(
          PLANNING_ERROR.TASK_NOTES_TOO_LONG,
          PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_NOTES_TOO_LONG]!,
          { description: descricao.slice(0, 40) },
        ),
      );
    }

    const extras = parseTaskExtras(entrada, descricao);
    if (!extras.ok) return err(extras.error);

    tarefas.push({
      description: descricao,
      ...(observacao === '' ? {} : { notes: observacao }),
      ...extras.value,
    });
  }

  if (tarefas.length > MAX_REPORT_TASKS) {
    return err(
      domainError(
        PLANNING_ERROR.TASK_TOO_MANY,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_TOO_MANY]!,
        { count: tarefas.length, max: MAX_REPORT_TASKS },
      ),
    );
  }

  return ok(tarefas);
}

export function parseReportNotes(notes: string): Result<string | undefined, DomainError> {
  const texto = notes.trim();
  if (texto === '') return ok(undefined);
  if (texto.length > MAX_REPORT_NOTES) {
    return err(
      domainError(
        PLANNING_ERROR.REPORT_NOTES_TOO_LONG,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_NOTES_TOO_LONG]!,
        { length: texto.length, max: MAX_REPORT_NOTES },
      ),
    );
  }
  return ok(texto);
}

/**
 * `Márcio (pedreiro); Maicon (pedreiro)` — como o RDO de referência escreve.
 *
 * A equipe (Fase 30, D5) sai com quem trabalhou entre parênteses, no lugar da função:
 * `Equipe Alvenaria 01 (Márcio, Maicon)`.
 */
export function describeWorkers(line: ExecutedFrontLine): string {
  return line.workers
    .map((worker) => {
      if (worker.members === undefined) return `${worker.name} (${worker.role})`;
      return worker.members.length === 0
        ? worker.name
        : `${worker.name} (${worker.members.join(', ')})`;
    })
    .join('; ');
}

/**
 * O que a avulsa carrega além da descrição (Fase 20): quem fez, quanto e por quanto tempo.
 *
 * Nome em branco é descartado, como a linha em branco; o resto é recusado quando não é o
 * que diz ser — quantidade negativa, hora que não é múltipla de quinze minutos.
 */
function parseTaskExtras(
  entrada: DailyReportTask,
  descricao: string,
): Result<Omit<DailyReportTask, 'description' | 'notes'>, DomainError> {
  const workers = (entrada.workers ?? []).map((nome) => nome.trim()).filter((nome) => nome !== '');
  if (
    workers.length > MAX_TASK_WORKERS ||
    workers.some((nome) => nome.length > MAX_TASK_WORKER_NAME)
  ) {
    return err(
      domainError(
        PLANNING_ERROR.TASK_WORKERS_INVALID,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_WORKERS_INVALID]!,
        { description: descricao.slice(0, 40), count: workers.length },
      ),
    );
  }

  const quantity = entrada.quantity;
  if (quantity !== undefined && (!Number.isFinite(quantity) || quantity < 0)) {
    return err(
      domainError(
        PLANNING_ERROR.TASK_QUANTITY_INVALID,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_QUANTITY_INVALID]!,
        { description: descricao.slice(0, 40), quantity },
      ),
    );
  }

  const unit = entrada.unit?.trim() ?? '';
  if (unit.length > MAX_TASK_UNIT) {
    return err(
      domainError(
        PLANNING_ERROR.TASK_UNIT_TOO_LONG,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_UNIT_TOO_LONG]!,
        { description: descricao.slice(0, 40) },
      ),
    );
  }

  const quarterHours = entrada.quarterHours;
  if (
    quarterHours !== undefined &&
    (!Number.isInteger(quarterHours) || quarterHours < 1 || quarterHours > MAX_TASK_QUARTER_HOURS)
  ) {
    return err(
      domainError(
        PLANNING_ERROR.TASK_HOURS_INVALID,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.TASK_HOURS_INVALID]!,
        { description: descricao.slice(0, 40), quarterHours },
      ),
    );
  }

  return ok({
    ...(workers.length === 0 ? {} : { workers }),
    ...(quantity === undefined ? {} : { quantity }),
    ...(unit === '' ? {} : { unit }),
    ...(quarterHours === undefined ? {} : { quarterHours }),
  });
}

/** `6 und`, `1,71 m²` — a quantidade da avulsa como texto do dia; vazio quando não há. */
export function describeTaskQuantity(task: DailyReportTask): string {
  if (task.quantity === undefined) return '';
  const numero = task.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return task.unit === undefined ? numero : `${numero} ${task.unit}`;
}

/** `2h`, `1,5h` — as horas da avulsa; vazio quando não há. */
export function describeTaskHours(task: DailyReportTask): string {
  if (task.quarterHours === undefined) return '';
  return `${(task.quarterHours / 4).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h`;
}

/**
 * A célula "Responsáveis e observações" da avulsa, na tela e no papel:
 * `Responsáveis: Carlos Henrique · 2h · Massa = 6`.
 */
export function describeTaskDetails(task: DailyReportTask): string {
  const partes: string[] = [];
  if (task.workers !== undefined && task.workers.length > 0) {
    partes.push(`Responsáveis: ${task.workers.join('; ')}`);
  }
  const horas = describeTaskHours(task);
  if (horas !== '') partes.push(horas);
  if (task.notes !== undefined) partes.push(task.notes);
  return partes.join(' · ');
}
